import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Prompt } from 'react-router-dom';

import {
  PART_FILE_UNITS,
  PREFERRED_UNIT_SYSTEMS,
  type PartFileUnits,
} from '@frontend/api';

import { WebViewerContextProvider } from 'modules/common/viewer/pdfViewer/WebViewerContext';

import { useFeatureFlagValue } from 'store/session/hooks';

import { useUnitSystemContext } from 'utils/measurement/context';

import { featureFlagKeys } from '../drawers/featureFlagDrawer/const/featureFlagKeys';
import FetchingInfoScreen from './FetchingInfoScreen';
import ImgViewer from './ImgViewer';
import MalwareDetectedScreen from './MalwareDetectedScreen';
import { NoFileToDisplayScreen } from './NoFileToDisplayScreen';
import { ProcessingFailedScreen } from './ProcessingFailedScreen';
import UnsupportedFileScreen from './UnsupportedFileScreen';
import VideoViewer from './VideoViewer';
import CADViewer from './cadViewer/CADViewer';
import {
  getAreCaliperToolSelectionsPresent,
  getAreSelectorToolSelectionsPresent,
} from './cadViewer/data/reducer';
import {
  getActiveFile,
  getActivePartPmiUrl,
  getIsFetchingViewerModel,
} from './data/reducer';
import { PDFViewer } from './pdfViewer/PDFViewer';
import { getAreUnsavedPdfAnnotationsPresent } from './pdfViewer/data/reducer';
import { getViewerDisplayParameters } from './utils';

type ViewerProps = {
  leftOffset?: string;
  isStandaloneViewer?: boolean;
  isPartLibraryViewer?: boolean;
  onUpdatePrintUnits?: (units: PartFileUnits) => void;
  showAllExtractions?: boolean;
  toggleShowAllExtractions?: () => void;
};

const ViewerInternal = ({
  leftOffset,
  isStandaloneViewer = false,
  isPartLibraryViewer = false,
  onUpdatePrintUnits,
  showAllExtractions,
  toggleShowAllExtractions,
}: ViewerProps) => {
  const activeFile = useSelector(getActiveFile);
  const activePartPmiUrl = useSelector(getActivePartPmiUrl);
  const areCaliperToolSelectionsPresent = useSelector(
    getAreCaliperToolSelectionsPresent,
  );
  const areSelectorToolSelectionsPresent = useSelector(
    getAreSelectorToolSelectionsPresent,
  );
  const areUnsavedPdfAnnotationsPresent = useSelector(
    getAreUnsavedPdfAnnotationsPresent,
  );
  const isFetchingViewerModel = useSelector(getIsFetchingViewerModel);

  const {
    containsVirus,
    filename,
    processingFailed,
    standardizedUrl,
    url,
    uuid,
    printUnits: printUnitsFile,
  } = activeFile ?? {};

  const { preferredUnitSystem } = useUnitSystemContext();

  // if print units not specified, default them to the preferred unit system
  const printUnits = useMemo(
    () =>
      printUnitsFile ??
      (preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? PART_FILE_UNITS.IN
        : PART_FILE_UNITS.MM),
    [printUnitsFile, preferredUnitSystem],
  );

  const {
    shouldShowCAD,
    shouldShowPdf,
    shouldShowNoFileToDisplayScreen,
    isImg,
    isVideo,
  } = useMemo(() => getViewerDisplayParameters(activeFile ?? {}), [activeFile]);

  const [shouldLoadCAD, setShouldLoadCAD] = useState(shouldShowCAD);
  const [hasShownFetching, setHasShownFetching] = useState(false);
  const [pmiUrlByPartFileUuid, setPmiUrlByPartFileUuid] = useState({});

  // This mounts the CAD Viewer "at most once" when any PartFile with geometry is viewed
  // The CAD Viewer is hidden, rather than unmounting, when other viewers are used (when new PartFile does not have geometry, i.e. CAD -> PDF)
  useEffect(() => {
    if (!shouldLoadCAD) {
      setShouldLoadCAD(shouldShowCAD);
    }
  }, [shouldLoadCAD, shouldShowCAD]);

  useEffect(() => {
    if (!uuid) {
      return;
    }
    const activePartFileUuid = uuid;
    if (!(activePartFileUuid in pmiUrlByPartFileUuid)) {
      setPmiUrlByPartFileUuid({
        ...pmiUrlByPartFileUuid,
        [activePartFileUuid]: activePartPmiUrl,
      });
    }
  }, [pmiUrlByPartFileUuid, activePartPmiUrl, uuid]);

  const [shouldRenderPDFViewer, setShouldRenderPDFViewer] =
    useState(shouldShowPdf);

  // This mounts the PDF Viewer "at most once" when any PartFile in the viewer is a PDF
  // The PDF Viewer is hidden, rather than unmounting, when other viewers are used
  useEffect(() => {
    if (!shouldRenderPDFViewer) {
      setShouldRenderPDFViewer(shouldShowPdf);
    }
  }, [shouldRenderPDFViewer, shouldShowPdf]);

  useEffect(() => {
    setHasShownFetching(
      (_hasShownFetching) => !isFetchingViewerModel && !_hasShownFetching,
    );
  }, [isFetchingViewerModel, setHasShownFetching]);

  let nonCadViewer = <></>;
  if (isImg) {
    nonCadViewer = <ImgViewer />;
  } else if (isVideo) {
    nonCadViewer = <VideoViewer />;
  } else if (shouldShowNoFileToDisplayScreen) {
    nonCadViewer = <NoFileToDisplayScreen key="noFileToDisplay" />;
  } else {
    nonCadViewer = <UnsupportedFileScreen key="UnsupportedFile" />;
  }

  const isRequirementsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );
  const isPdfRedactionEnabled = useFeatureFlagValue(
    featureFlagKeys.isPdfRedactionEnabled,
  );
  const isBeamsEnabled = useFeatureFlagValue(featureFlagKeys.isBeamsEnabled);

  return (
    <>
      <Prompt
        when={
          !!(
            areUnsavedPdfAnnotationsPresent ||
            areCaliperToolSelectionsPresent ||
            areSelectorToolSelectionsPresent
          )
        }
        message={
          'You have unsaved annotations. Are you sure you want to continue?'
        }
      />
      {containsVirus ? (
        <MalwareDetectedScreen />
      ) : (
        <>
          {isFetchingViewerModel && !hasShownFetching && <FetchingInfoScreen />}
          {shouldLoadCAD && (
            <CADViewer
              leftOffset={leftOffset}
              isPaused={!shouldShowCAD}
              pmiUrlByPartFileUuid={pmiUrlByPartFileUuid}
            />
          )}
          {shouldRenderPDFViewer && (
            <PDFViewer
              activeFileUuid={uuid ?? null}
              printUnits={printUnits}
              onUpdatePrintUnits={onUpdatePrintUnits}
              filename={filename ?? null}
              url={(standardizedUrl || url) ?? null}
              isStandaloneViewer={isStandaloneViewer}
              isPartLibraryViewer={isPartLibraryViewer}
              isPaused={!shouldShowPdf}
              isRequirementsReviewEnabled={isRequirementsReviewEnabled}
              isPdfRedactionEnabled={isPdfRedactionEnabled}
              isBeamsEnabled={isBeamsEnabled}
              showAllExtractions={showAllExtractions}
              toggleShowAllExtractions={toggleShowAllExtractions}
            />
          )}
          {processingFailed ? (
            <ProcessingFailedScreen key="ProcessingFailed" />
          ) : (
            !shouldShowCAD && nonCadViewer
          )}
        </>
      )}
    </>
  );
};

const Viewer = (props: ViewerProps) => {
  return (
    <WebViewerContextProvider>
      <ViewerInternal {...props} />
    </WebViewerContextProvider>
  );
};

export default Viewer;
